'use es6';

import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

const resetItemVisibility = lis => lis.forEach(li => {
  li.classList.add('unshown');
  li.classList.remove('hidden');
});
/**
 * Partitions items into collections that will each fit inside the vertical
 * bounds of wrapper. Returns an array of integers.
 *
 * Exported for testing only
 *
 * @param {DOMRect} wrapper
 * @param {Array<DomRect>} items
 * @returns {Array<Number>} partitions
 */


export const verticalPartitions = (wrapper, items) => {
  let top = wrapper.top;
  return items.reduce((parts, item) => {
    if (item.bottom - top <= wrapper.height) {
      parts[parts.length - 1]++;
    } else {
      parts.push(1);
      top = item.top;
    }

    return parts;
  }, [0]);
};

const getPartitions = (ul, container) => {
  const lis = [...ul.querySelectorAll('li')];
  const wrapper = container.getBoundingClientRect();
  resetItemVisibility(lis);
  return verticalPartitions(wrapper, lis.map(li => li.getBoundingClientRect()));
};
/**
 * Sets visibility classes on each list item based on current partition info.
 *
 * Exported for testing only
 *
 * @param {Array<Element>} lis - li elements
 * @param {Number} index
 * @param {Array<Number>} partitions
 */


export const updateItems = (lis, index, partitions) => {
  const place = partitions.reduce((memo, part, i) => i < index ? memo + part : memo, 0);
  const end = place + partitions[index];
  resetItemVisibility(lis);

  for (let i = 0; i < lis.length; i++) {
    const li = lis[i];

    if (i < place) {
      li.classList.add('hidden');
    }

    if (i === end) {
      break;
    }

    if (i >= place) {
      li.classList.remove('unshown');
    }
  }
};
export const useVerticalFitPagination = (ul, container) => {
  const [index, setIndex] = useState(0);
  const [partitions, setPartitions] = useState([]);
  const hasDir = useMemo(() => {
    return {
      [-1]: index - 1 >= 0,
      [1]: index + 1 < partitions.length
    };
  }, [index, partitions]);
  const proceed = useCallback(dir => {
    if (hasDir[dir]) {
      setIndex(index + dir);
    }
  }, [setIndex, hasDir, index]);
  const update = useCallback(() => setPartitions(getPartitions(ul.current, container.current)), [setPartitions, ul, container]); // useLayoutEffect avoids flicker when resizing window

  useLayoutEffect(() => {
    const lis = [...ul.current.querySelectorAll('li')];
    updateItems(lis, index, partitions);
  }, [index, partitions, ul]);
  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update]);
  /**
   * update() again, in case the first partition creation adds arrows which
   * change the available size. Only happens at a particular screen size
   * e.g. innerWidth ~ 992px on Chrome
   */

  useEffect(() => {
    const timeout = window.setTimeout(update, 0);
    return () => window.clearTimeout(timeout);
  }, [update]);
  return {
    hasPartitions: partitions.length > 1,
    hasDir,
    proceed
  };
};